import { Alert, Box } from '@mui/material';
import UPButton from 'components/button';
import UPInput from 'components/input';
import PasswordsMatchText from 'components/password-strength/PasswordsMatchText';
import PasswordStrength from 'components/password-strength/PasswordStrength';
import { CloseAlertIcon, SectionContainer } from 'modules/companies/styles';
import { useCallback, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store';
import { changePasswordAction } from 'store/actions/profile-actions';
import {
  GENERAL__CANCEL_BUTTON,
  GENERAL__ERROR_MESSAGE,
  GENERAL__SAVE_BUTTON,
  PROFILE__CONFIRM_NEW_PASSWORD,
  PROFILE__CURRENT_PASSWORD,
  PROFILE__NEW_PASSWORD,
  PROFILE__UPDATE_PASSWORD_SUCCESSFUL,
} from 'translations/constants';
import IPasswordChange from 'types/profile/IPasswordChange';
import { validatePassword } from 'utils/helpers';
import { ReactComponent as CloseIcon } from '../../assets/icons/Close.svg';
import { LeftContainer } from './styles';

const PasswordChangeInitialState: IPasswordChange = {
  currentPassword: '',
  newPassword: '',
  confirmedNewPassword: '',
};

const ChangePassword = (): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
    getValues,
    watch,
  } = useForm<IPasswordChange>({
    defaultValues: PasswordChangeInitialState,
    mode: 'onChange',
  });

  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  const [banner, setBanner] = useState<'error' | 'success' | null>(null);

  const newPasswordWatch = watch('newPassword');
  const confirmedPasswordWatch = watch('confirmedNewPassword');

  const onChangePassword: SubmitHandler<IPasswordChange> = useCallback(async () => {
    const { currentPassword: oldPassword, newPassword } = getValues();
    try {
      const success = await dispatch(changePasswordAction({ oldPassword, newPassword })).unwrap();
      if (success) {
        setBanner('success');
      } else {
        setBanner('error');
      }
    } catch {
      setBanner('error');
    } finally {
      reset(PasswordChangeInitialState);
      setIsButtonDisabled(false);
    }
  }, [getValues, dispatch, reset]);

  return (
    <SectionContainer>
      <LeftContainer>
        <Box mt={3} sx={{ width: '100%' }}>
          <Controller
            name="currentPassword"
            control={control}
            rules={{ required: true, maxLength: 100 }}
            render={({ field }) => (
              <UPInput
                value={field.value}
                label={t(PROFILE__CURRENT_PASSWORD)}
                placeholder={t(PROFILE__CURRENT_PASSWORD)}
                required
                onChange={field.onChange}
                type="password"
              />
            )}
          />
        </Box>

        <Box mt={3} sx={{ width: '100%' }}>
          <Controller
            name="newPassword"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <UPInput
                value={field.value}
                label={t(PROFILE__NEW_PASSWORD)}
                placeholder={t(PROFILE__NEW_PASSWORD)}
                error={!!errors.newPassword}
                required
                onChange={field.onChange}
                type="password"
              />
            )}
          />
          <Box width="100%" height={20}>
            <PasswordStrength password={newPasswordWatch} />
          </Box>
        </Box>

        <Box mt={3} mb={3} sx={{ width: '100%' }}>
          <Controller
            name="confirmedNewPassword"
            control={control}
            rules={{
              required: true,
              maxLength: 100,
            }}
            render={({ field }) => (
              <UPInput
                value={field.value}
                label={t(PROFILE__CONFIRM_NEW_PASSWORD)}
                placeholder={t(PROFILE__CONFIRM_NEW_PASSWORD)}
                error={!!errors.confirmedNewPassword}
                required
                onChange={field.onChange}
                type="password"
              />
            )}
          />
          <Box width="100%" height={20}>
            {confirmedPasswordWatch.length > 0 && (
              <PasswordsMatchText passwordsMatch={newPasswordWatch === confirmedPasswordWatch} />
            )}
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <UPButton
            text={t(GENERAL__SAVE_BUTTON)}
            disabled={
              isButtonDisabled || !validatePassword(newPasswordWatch) || newPasswordWatch !== confirmedPasswordWatch
            }
            onClick={() => {
              setIsButtonDisabled(true);
              handleSubmit(onChangePassword)();
            }}
          />
          <UPButton text={t(GENERAL__CANCEL_BUTTON)} onClick={() => reset(PasswordChangeInitialState)} outlined />
        </Box>
        {banner === 'success' && (
          <Alert
            severity="success"
            icon={false}
            variant="filled"
            sx={{ width: '100%', marginTop: '16px' }}
            action={
              <CloseAlertIcon
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  setBanner(null);
                }}
              >
                <CloseIcon fill="white" />
              </CloseAlertIcon>
            }
          >
            {t(PROFILE__UPDATE_PASSWORD_SUCCESSFUL)}
          </Alert>
        )}
        {banner === 'error' && (
          <Alert
            severity="error"
            icon={false}
            variant="filled"
            sx={{ width: '100%', marginTop: '16px' }}
            action={
              <CloseAlertIcon
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  setBanner(null);
                }}
              >
                <CloseIcon fill="white" />
              </CloseAlertIcon>
            }
          >
            {t(GENERAL__ERROR_MESSAGE)}
          </Alert>
        )}
      </LeftContainer>
    </SectionContainer>
  );
};

export default ChangePassword;
